import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Container, PageTitle, GameContainer, Button, FlexContainer, ChoiceButton } from '../../styles/StyledComponents';
import { useGameContext } from '../../context/GameContext';

const Character = styled.div`
  width: 150px;
  height: 150px;
  background-color: var(--secondary-color);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  margin: 2rem auto;
  position: relative;

  &:before {
    content: '';
    position: absolute;
    width: 30px;
    height: 30px;
    background-color: var(--secondary-color);
    border-radius: 50%;
    top: -15px;
    left: 60px;
  }

  &:after {
    content: '';
    position: absolute;
    width: 30px;
    height: 30px;
    background-color: var(--secondary-color);
    border-radius: 50%;
    top: -15px;
    right: 60px;
  }
`;

const QuestionContainer = styled.div`
  font-size: 2rem;
  font-family: var(--font-family) !important;
  text-align: center;
  margin: 2rem 0;
  padding: 1rem;
  background-color: #f9f9f9;
  border-radius: var(--border-radius);
  box-shadow: var(--box-shadow);

  @media (max-width: 768px) {
    font-size: 1.5rem;
    padding: 0.75rem;
  }
`;

const ChoicesContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  margin: 2rem 0;
`;

const FeedbackMessage = styled.div<{ isCorrect: boolean }>`
  font-size: 1.5rem;
  font-family: var(--font-family) !important;
  text-align: center;
  padding: 1rem;
  margin: 1rem 0;
  border-radius: var(--border-radius);
  background-color: ${props => props.isCorrect ? 'rgba(76, 175, 80, 0.2)' : 'rgba(244, 67, 54, 0.2)'};
  color: ${props => props.isCorrect ? 'var(--success-color)' : 'var(--error-color)'};

  @media (max-width: 768px) {
    font-size: 1.2rem;
    padding: 0.75rem;
  }
`;

const ScoreDisplay = styled.div`
  font-size: 1.2rem;
  font-family: var(--font-family) !important;
  text-align: right;
  margin-bottom: 1rem;

  span {
    font-weight: bold;
    color: var(--primary-color);
  }
`;

interface Question {
  operation: 'addition' | 'subtraction';
  num1: number;
  num2: number;
  choices: number[];
  correctAnswer: number;
}

const generateQuestion = (maxNumber: number = 20): Question => {
  // For nearly 5-year-olds, let's keep problems simple but engaging
  const num1 = Math.floor(Math.random() * (maxNumber - 1)) + 1;

  // For subtraction, ensure num2 is smaller than num1
  const operation = Math.random() > 0.5 ? 'addition' : 'subtraction';
  const num2 = operation === 'subtraction'
    ? Math.floor(Math.random() * num1) + 1
    : Math.floor(Math.random() * (maxNumber - num1)) + 1;

  const correctAnswer = operation === 'addition' ? num1 + num2 : num1 - num2;

  // Generate answer choices with the correct answer among them
  const choices = [correctAnswer];

  while (choices.length < 4) {
    const randomChoice = Math.floor(Math.random() * maxNumber) + 1;
    if (!choices.includes(randomChoice)) {
      choices.push(randomChoice);
    }
  }

  // Shuffle the choices
  for (let i = choices.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [choices[i], choices[j]] = [choices[j], choices[i]];
  }

  return {
    operation,
    num1,
    num2,
    choices,
    correctAnswer
  };
};

const NumberQuest: React.FC = () => {
  const [question, setQuestion] = useState<Question>(generateQuestion());
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [isCorrect, setIsCorrect] = useState<boolean | null>(null);
  const [score, setScore] = useState<number>(0);
  const { updateProgress } = useGameContext();

  const handleAnswerSelection = (answer: number) => {
    if (selectedAnswer !== null) return; // Prevent selecting another answer

    setSelectedAnswer(answer);
    const correct = answer === question.correctAnswer;
    setIsCorrect(correct);

    if (correct) {
      setScore(prevScore => prevScore + 1);
      playSound('success');
    } else {
      playSound('error');
    }

    // Save progress to context
    updateProgress('number-quest', score + (correct ? 1 : 0));

    // Move to next question after 2 seconds
    setTimeout(() => {
      setSelectedAnswer(null);
      setIsCorrect(null);
      setQuestion(generateQuestion());
    }, 2000);
  };

  const playSound = (type: 'success' | 'error' | 'click') => {
    // In a real implementation, we would play actual sounds here
    console.log(`Playing ${type} sound`);
    // Example of how to play a sound:
    // const audio = new Audio(`/assets/audio/${type}.mp3`);
    // audio.play();
  };

  const getQuestionText = () => {
    const { operation, num1, num2 } = question;

    if (operation === 'addition') {
      return `You have ${num1} coins, and you found ${num2} more! How many coins do you have now?`;
    } else {
      return `You have ${num1} coins, and you gave away ${num2}. How many coins do you have left?`;
    }
  };

  return (
    <Container>
      <PageTitle>Number Quest</PageTitle>

      <GameContainer>
        <FlexContainer justify="space-between">
          <Link to="/">
            <Button variant="secondary">Back to Games</Button>
          </Link>

          <ScoreDisplay>
            Score: <span>{score}</span>
          </ScoreDisplay>
        </FlexContainer>

        <Character>🧙</Character>

        <QuestionContainer>
          {getQuestionText()}
        </QuestionContainer>

        <ChoicesContainer>
          {question.choices.map((choice, index) => (
            <ChoiceButton
              key={index}
              isSelected={selectedAnswer === choice}
              isCorrect={selectedAnswer === choice && isCorrect === true}
              onClick={() => handleAnswerSelection(choice)}
              disabled={selectedAnswer !== null}
            >
              {choice}
            </ChoiceButton>
          ))}
        </ChoicesContainer>

        {isCorrect !== null && (
          <FeedbackMessage isCorrect={isCorrect}>
            {isCorrect
              ? '✨ Great job! That\'s correct! ✨'
              : `Oops! The correct answer is ${question.correctAnswer}.`}
          </FeedbackMessage>
        )}
      </GameContainer>
    </Container>
  );
};

export default NumberQuest;
